"use client";

import {
	type ComponentProps,
	type PropsWithChildren,
	type ReactNode,
	useState,
} from "react";
import {
	Input as AriaInput,
	TextField as AriaTextField,
	Button,
	Label,
} from "react-aria-components";
import { type VariantProps, tv } from "tailwind-variants";

import { Eye } from "@/components/icons/eye";
import { useHandlersWithStatus } from "@/components/status";

type TextFieldProps = Omit<
	ComponentProps<typeof AriaTextField>,
	keyof PropsWithChildren
>;
type InputProps = Omit<
	ComponentProps<typeof AriaInput>,
	keyof TextFieldProps | keyof PropsWithChildren
>;

type Props = PropsWithChildren<
	TextFieldProps &
		InputProps &
		VariantProps<typeof variants> & {
			label?: ReactNode;
			className?: string;
		}
>;

export function Input({
	label,
	placeholder,
	children,
	variant,
	className,
	...props
}: Props) {
	const { input, wrapper, textField } = variants({ variant });
	const handlers = useHandlersWithStatus({ onChange: props.onChange }, true);

	const [type, setType] = useState(props.type);

	return (
		<AriaTextField
			{...props}
			{...handlers}
			type={type}
			className={textField({ className })}
		>
			{label ? <Label className="text-muted-foreground">{label}</Label> : null}
			<div className={wrapper()}>
				{children}
				<AriaInput placeholder={placeholder} className={input()} />
				{props.type === "password" ? (
					<Button
						className="mr-3"
						onPress={() =>
							setType((type) => (type === "password" ? "text" : "password"))
						}
					>
						<Eye className="size-6 text-muted-foreground" />
					</Button>
				) : null}
			</div>
		</AriaTextField>
	);
}

const variants = tv({
	slots: {
		input: "w-full rounded-full py-3 px-5 outline-none animate",
		wrapper: "w-full relative flex items-center rounded-full animate",
		textField: "w-105 grid gap-2 group animate",
	},
	variants: {
		variant: {
			default: {
				wrapper: "outline focus-within:outline-focus",
				textField: "w-105",
			},
			search: {
				wrapper: "h-10 bg-white/4",
				input: "pl-0.5",
			},
			searchSmall: {
				wrapper: "h-8 bg-white/4",
				input: "pl-0.5 px-2",
				textField: "min-w-0 w-full px-2",
			},
		},
	},
	defaultVariants: {
		variant: "default",
	},
});
